import { useEffect, useState, useCallback } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "hopsy_pwa_dismissed_at";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - parseInt(raw, 10) < DISMISS_COOLDOWN_MS;
  } catch { return false; }
}

async function registerStorefrontSW() {
  if (!("serviceWorker" in navigator)) return null;
  // Avoid hijacking /admin scope; register at "/" scope.
  try {
    return await navigator.serviceWorker.register("/customer-sw.js", { scope: "/" });
  } catch (e) {
    console.warn("[pwa] storefront SW register failed", e);
    return null;
  }
}

export default function StorefrontPWA() {
  const [installEvent, setInstallEvent] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(recentlyDismissed());

  useEffect(() => {
    // Inject the customer manifest + apple touch icon (admin PWA uses its
    // own manifest on /admin pages; this one only lives on storefront).
    if (!document.querySelector('link[rel="manifest"][data-customer="1"]')) {
      const link = document.createElement("link");
      link.rel = "manifest";
      link.href = "/customer-manifest.webmanifest";
      link.setAttribute("data-customer", "1");
      document.head.appendChild(link);
    }
    if (!document.querySelector('link[rel="apple-touch-icon"][data-customer="1"]')) {
      const apple = document.createElement("link");
      apple.rel = "apple-touch-icon";
      apple.href = "/hopsy-apple-touch-icon.webp";
      apple.setAttribute("data-customer", "1");
      document.head.appendChild(apple);
    }

    registerStorefrontSW();

    // Self-heal stale shells: when our SW activates a new version it posts
    // back HOPSY_SW_UPDATED — reload the page so the user picks up fresh
    // HTML + JS bundles and any new product data immediately.
    const onSwMessage = (e) => {
      if (e?.data?.type === "HOPSY_SW_UPDATED") {
        const lastReload = parseInt(sessionStorage.getItem("hopsy_sw_reload_at") || "0", 10);
        // Hard-cap to one reload per minute so we never get into a loop.
        if (Date.now() - lastReload > 60_000) {
          try { sessionStorage.setItem("hopsy_sw_reload_at", String(Date.now())); } catch { /* noop */ }
          // Surface a tiny transparency toast so users see WHY the page is
          // reloading (builds trust that the app self-maintains).
          try {
            toast.success("App updated · Reloading…", {
              duration: 1400,
              position: "bottom-center",
              className: "hopsy-sw-update-toast",
            });
          } catch { /* sonner not yet mounted — fine, just reload */ }
          window.setTimeout(() => window.location.reload(), 1500);
        }
      }
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onSwMessage);
    }
    const onBip = (e) => { e.preventDefault(); setInstallEvent(e); };
    window.addEventListener("beforeinstallprompt", onBip);

    const onInstalled = () => { setIsInstalled(true); setInstallEvent(null); };
    window.addEventListener("appinstalled", onInstalled);

    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", onSwMessage);
      }
    };
  }, []);

  const install = useCallback(async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") {
      setIsInstalled(true);
      setInstallEvent(null);
    } else {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
      setDismissed(true);
    }
  }, [installEvent]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setDismissed(true);
  }, []);

  if (isInstalled || dismissed || !installEvent) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-40 bg-forest text-cream rounded-2xl shadow-xl border border-forest-hover overflow-hidden animate-fade-up"
      data-testid="storefront-pwa-install"
      role="dialog"
      aria-label="Install the Hopsy app"
    >
      <div className="p-4 md:p-5 flex gap-3 items-start">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-sun flex items-center justify-center">
          <Smartphone className="w-5 h-5 text-forest" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-lg tracking-tightest leading-snug">
            Install the Hopsy app
          </p>
          <p className="text-[12px] text-cream/80 font-light mt-1 leading-relaxed">
            One-tap reorder, offline browsing, and gentle nudges before you run out of hay.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={install}
              data-testid="storefront-pwa-install-btn"
              className="inline-flex items-center gap-1.5 bg-sun text-forest font-semibold px-4 py-2 rounded-full text-[11px] uppercase tracking-widest hover:bg-sun-hover transition-all hover:-translate-y-0.5"
            >
              <Download className="w-3.5 h-3.5" /> Install
            </button>
            <button
              onClick={dismiss}
              data-testid="storefront-pwa-dismiss"
              className="text-[11px] uppercase tracking-widest text-cream/60 hover:text-cream px-3 py-2"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-2 right-2 p-1.5 text-cream/50 hover:text-cream"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
